/**
 * QdsBaseMenu
 * 
 * A base class for menu components that provides common functionality for
 * organizing and displaying hierarchical menu items.
 * 
 * This is not a web component itself but serves as a base class for:
 * - QdsHierarchicalMenu (dropdown menu)
 * - QdsHorizontalMenu (horizontal navigation)
 * - QdsSidebarMenu (sidebar navigation)
 * 
 * @abstract
 */

import { HierarchicalMenuItem } from '../../types/index.js';
import { renderQdsIcon } from '../shared/icon-utils.js';

export function resolveDefaultAssetRoot(): string {
  const currentUrl = new URL(import.meta.url);
  const bundledAssetPathMatch = currentUrl.pathname.match(/^(.*\/assets)\/(?:components|chunks)(?:\/.*)?$/);

  if (bundledAssetPathMatch) {
    currentUrl.pathname = `${bundledAssetPathMatch[1]}/`;
    currentUrl.search = '';
    currentUrl.hash = '';
    return currentUrl.toString();
  }

  const sourceTreeAssetPathMatch = currentUrl.pathname.match(/^(.*)\/src\/(?:components|services)(?:\/.*)?$/);

  if (sourceTreeAssetPathMatch) {
    currentUrl.pathname = `${sourceTreeAssetPathMatch[1]}/content/assets/`;
    currentUrl.search = '';
    currentUrl.hash = '';
    return currentUrl.toString();
  }

  return new URL('../../', import.meta.url).toString();
}

export const DEFAULT_ASSET_ROOT = resolveDefaultAssetRoot();
const DEFAULT_ICON_ROOT = new URL('images/', DEFAULT_ASSET_ROOT).toString();

/**
 * Options for generating link HTML
 */
export interface GenerateLinkOptions {
  className?: string;
  additionalAttributes?: string;
  includeIcon?: boolean;
  wrapContent?: boolean;
}

export class QdsBaseMenu {
  // Public properties that are part of the API
  public menuData: HierarchicalMenuItem[];
  public iconRoot: string;
  public searchTerm: string;
  public isSearchMode: boolean;
  public searchResults: HierarchicalMenuItem[];

  // Private properties
  private _useIcons: boolean;
  /*
  <ul class="customListStyle"><li><p class="noSpacingAbove noSpacingBelow" style="text-align:justify;" data-text-type="noSpacing"><a href="/sites/QCGShared/_layouts/15/viewlsts.aspx?view=14" data-interception="off" target="_blank" rel="noopener noreferrer">QCG Shared</a></p></li><li><p class="noSpacingAbove noSpacingBelow" style="text-align:justify;" data-text-type="noSpacing"><a href="/sites/QLCPShared/QLCP%20Shared/Forms/AllItems.aspx" data-interception="off" target="_blank" rel="noopener noreferrer">QLCP Shared</a></p></li><li><p class="noSpacingAbove noSpacingBelow" style="text-align:justify;" data-text-type="noSpacing"><a href="/sites/QSPShared/QSP%20Shared/Forms/AllItems.aspx">QSP Shared</a></p></li><li><p class="noSpacingAbove noSpacingBelow" style="text-align:justify;" data-text-type="noSpacing"><a href="/sites/ExternalShared/External%20Shared/Forms/AllItems.aspx" data-interception="off" target="_blank" rel="noopener noreferrer">External Shared</a></p></li><li><p class="noSpacingAbove noSpacingBelow" style="text-align:justify;" data-text-type="noSpacing"><a href="/sites/GCOffice/General%20Counsel%20Office/Forms/AllItems.aspx?npsAction=createList" data-interception="off" target="_blank" rel="noopener noreferrer">General Counsel &amp; GCO</a></p></li><li><p class="noSpacingAbove noSpacingBelow" style="text-align:justify;" data-text-type="noSpacing"><a href="/sites/TechTeam/Tech%20Team%20Box%20Drive%20folders/Forms/AllItems.aspx" data-interception="off" target="_blank" rel="noopener noreferrer">Quantum Technical</a></p></li><li><p class="noSpacingAbove noSpacingBelow" style="text-align:justify;" data-text-type="noSpacing"><a href="/sites/BusinessDevelopment/Business%20Development/Forms/AllItems.aspx?npsAction=createList" data-interception="off" target="_blank" rel="noopener noreferrer">Business Development&nbsp;</a></p></li><li><p class="noSpacingAbove noSpacingBelow" style="text-align:justify;" data-text-type="noSpacing"><a href="/sites/SpecialProjects/Special%20Projects/Forms/AllItems.aspx?npsAction=createList" data-interception="off" target="_blank" rel="noopener noreferrer">Special Projects</a></p></li><li><p class="noSpacingAbove noSpacingBelow" style="text-align:justify;" data-text-type="noSpacing"><a href="/sites/RoyaltyBusiness/Royalty%20Business/Forms/AllItems.aspx?npsAction=createList%20" data-interception="off" target="_blank" rel="noopener noreferrer">Royalty Business</a></p></li><li><p class="noSpacingAbove noSpacingBelow" style="text-align:justify;" data-text-type="noSpacing"><a href="/sites/RenewableRoyalties/Renewables/Forms/AllItems.aspx?npsAction=createList%20" data-interception="off" target="_blank" rel="noopener noreferrer">Renewables</a></p></li><li><p class="noSpacingAbove noSpacingBelow" style="text-align:justify;" data-text-type="noSpacing"><a href="/sites/ExpenseReportingApprovals/Expense%20Reporting%20Approvals/Forms/AllItems.aspx?npsAction=createList%20" data-interception="off" target="_blank" rel="noopener noreferrer">Expense Reporting Approvals</a></p></li></ul>
  */

  /**
   * Creates an instance of QdsBaseMenu
   */
  constructor() {
    // Default menu data in hierarchical structure
    this.menuData = [];

    // Base URL for icons
    this.iconRoot = DEFAULT_ICON_ROOT;

    // Whether to show icons in menu items
    this._useIcons = false;

    // Current search term
    this.searchTerm = "";

    // Flag for search mode
    this.isSearchMode = false;

    // Filtered search results
    this.searchResults = [];
  }

  /**
   * Utility function to organize flat data into a hierarchical tree structure
   * based on specified attributes (backward compatibility)
   * 
   * @param data - Array of menu items to organize
   * @param hierarchyAttrs - Array of attribute names to organize by, in order of hierarchy
   * @returns Hierarchical array of menu items
   */
  organizeIntoTree(data: HierarchicalMenuItem[], hierarchyAttrs: string[]): HierarchicalMenuItem[] {
    if (!Array.isArray(hierarchyAttrs) || hierarchyAttrs.length === 0 || !Array.isArray(data)) return data;

    const result: HierarchicalMenuItem[] = [];
    const groupedItems: { [key: string]: HierarchicalMenuItem[] } = {};
    const ungroupedItems: HierarchicalMenuItem[] = [];

    // Get the first level attribute to group by
    const primaryAttr = hierarchyAttrs[0];

    // Group items by the primary attribute
    data?.forEach(item => {
      if (item[primaryAttr]) {
        const key = item[primaryAttr] as string;
        if (!groupedItems[key]) {
          groupedItems[key] = [];
        }
        groupedItems[key].push(item);
      } else {
        ungroupedItems.push(item);
      }
    });

    // Add ungrouped items directly to the result as top-level items
    ungroupedItems.forEach(item => {
      result.push({ ...item });
    });

    // Create parent items for each group and add children
    Object.keys(groupedItems).forEach(key => {
      const groupItems = groupedItems[key];

      // Create parent item for this group
      const parentItem: HierarchicalMenuItem = {
        name: key,
        linkUrl: "#", // Placeholder URL
        items: []
      };

      // For multi-level hierarchy, recursively organize child items
      if (hierarchyAttrs.length > 1) {
        const nextLevelItems = this.organizeIntoTree(
          groupItems,
          hierarchyAttrs.slice(1)
        );

        // Add as children of this parent
        nextLevelItems.forEach(childItem => {
          if (parentItem.items) {
            parentItem.items.push(childItem);
          }
        });
      } else {
        // For single level hierarchy, add the group items directly as children
        groupItems.forEach(item => {
          if (parentItem.items) {
            parentItem.items.push({ ...item });
          }
        });
      }

      // Add the parent item with its children to the result
      result.push(parentItem);
    });

    return result;
  }

  /**
   * Transforms hierarchical menu structure into flat array
   * Useful for searching and filtering
   * 
   * @returns Flat array of all menu items
   */
  getAllItems(): HierarchicalMenuItem[] {
    const result: HierarchicalMenuItem[] = [];

    /**
     * Helper function to extract all items from the tree
     */
    const extractItems = (items: HierarchicalMenuItem[]) => {
      items?.forEach(item => {
        // Add the current item
        result.push(item);

        // Recursively process child items if any
        if (item.items && item.items.length > 0) {
          extractItems(item.items);
        }
      });
    };

    extractItems(this.menuData);
    return result;
  }

  /**
   * Filter menu items based on search term
   * 
   * @param searchTerm - The search term to filter by
   * @returns Filtered menu items
   */
  filterMenuItems(searchTerm: string): HierarchicalMenuItem[] {
    if (!searchTerm || searchTerm.trim() === '') {
      this.isSearchMode = false;
      return [];
    }

    this.isSearchMode = true;
    const term = searchTerm.toLowerCase().trim();
    const allItems = this.getAllItems();

    return allItems.filter(item => {
      // Search in name, description and businessFunction
      const nameMatch = item.name?.toLowerCase().includes(term);
      const descMatch = item.description?.toLowerCase().includes(term);
      const funcMatch = item.businessFunction?.toLowerCase().includes(term);

      return nameMatch || descMatch || funcMatch;
    });
  }

  /**
   * Handle search input event
   * 
   * @param searchTerm - The search term
   */
  handleSearch(searchTerm: string): HierarchicalMenuItem[] {
    this.searchTerm = searchTerm;

    if (searchTerm.trim() === '') {
      this.isSearchMode = false;
      this.searchResults = [];
    } else {
      this.searchResults = this.filterMenuItems(searchTerm);
    }

    // Keep only unique items in search results
    const uniqueResults = new Map<string, HierarchicalMenuItem>();
    this.searchResults.forEach(item => {
      if (!uniqueResults.has(item.name)) {
        uniqueResults.set(item.name, item);
      }
    });

    // Update search results with unique items
    if (uniqueResults.size > 0)
      this.searchResults = Array.from(uniqueResults.values());

    return this.searchResults;
  }

  /**
   * Get whether icons are displayed
   * @returns True if icons are displayed, false otherwise
   */
  get useIcons(): boolean {
    return this._useIcons;
  }

  /**
   * Set whether icons should be displayed
   * @param value - True to display icons, false to hide them
   */
  set useIcons(value: boolean) {
    this._useIcons = Boolean(value);
  }

  /**
   * For debugging purposes, print menu structure
   */
  printTree(): void {
    console.log("Menu Structure:", JSON.stringify(this.menuData, null, 2));
  }

  /**
   * Resolves a menu icon path against the configured icon root.
   * Asset paths that already start at the site root should not be prefixed again.
   * 
   * @param icon - Icon path from the menu item
   * @returns Resolved icon URL
   */
  resolveIconSrc(icon: string): string {
    if (!icon) {
      return '';
    }

    // Absolute (external or protocol-relative) URLs are used verbatim.
    if (/^(https?:)?\/\//.test(icon)) {
      return icon;
    }

    // Normalize a leading "./" or "/" so both "assets/..." and "/dev/assets/..."
    // are treated as deployment-relative asset paths. This is what lets icons
    // resolve correctly under a path prefix (e.g. /dev): a root-absolute
    // "/dev/assets/..." would otherwise point at the site root and 404.
    const normalizedIcon = icon.replace(/^\.?\//, '');

    if (normalizedIcon.startsWith('assets/')) {
      return new URL(normalizedIcon.slice('assets/'.length), DEFAULT_ASSET_ROOT).toString();
    }

    // Other root-absolute paths (e.g. "/custom/icon.svg") are intentional and
    // left untouched.
    if (icon.startsWith('/')) {
      return icon;
    }

    const normalizedRoot = this.iconRoot.endsWith('/') ? this.iconRoot : `${this.iconRoot}/`;
    return `${normalizedRoot}${normalizedIcon}`;
  }

  /**
   * Takes a menu item and generates the appropriate icon HTML
   * @param item - Menu item object
   * @returns HTML string for the icon or empty string if icons not enabled
   */
  generateIconHtml(item: HierarchicalMenuItem): string {
    if (!this._useIcons || !item.icon) return '';

    return this.renderIconHtml(item);
  }

  /** Generates icon HTML for a menu item regardless of the `use-icons` setting. */
  renderIconHtml(item: HierarchicalMenuItem): string {
    if (!item.icon) return '';

    const namedIcon = renderQdsIcon(item.icon, 'icon');
    if (namedIcon) return namedIcon;

    const iconSrc = this.resolveIconSrc(item.icon);

    return `<img src="${iconSrc}" class="icon" alt="${item.name} icon">`;
  }

  /**
   * Generates HTML for a menu item link with consistent attributes
   * @param item - Menu item object
   * @param options - Additional options for the link
   * @returns HTML string for the link
   */
  generateLinkHtml(item: HierarchicalMenuItem, options: GenerateLinkOptions = {}): string {
    const {
      className = '',
      additionalAttributes = '',
      includeIcon = this._useIcons,
      wrapContent = false
    } = options;

    // Generate target attribute with "_blank" as default if not specified.
    // A disabled row is a span: no href, no target, no default action.
    const disabled = item.disabled === true;
    const targetAttr = disabled ? '' : ` target="${item.target || '_blank'}"`;

    // Generate icon if enabled
    const iconHtml = includeIcon && item.icon ? this.generateIconHtml(item) : '';

    // Format content based on wrapping option
    const content = wrapContent
      ? `<span class="item-text">${item.name}</span>`
      : item.name;

    // Count/status chip after the label; hosts style .menu-item-badge.
    const badgeHtml =
      item.badge === undefined || item.badge === null || item.badge === ''
        ? ''
        : `<span class="menu-item-badge">${item.badge}</span>`;

    // Host-driven active state (e.g. derived from the router) joins the
    // click-set one, which is also class "active".
    const stateClasses = `${item.active === true ? ' active' : ''}${disabled ? ' disabled' : ''}`;
    const disabledAttrs = disabled ? ' aria-disabled="true" tabindex="-1"' : '';

    // Build the link HTML
    const [openTag, closeTag] = disabled ? ['<span', '</span>'] : ['<a', '</a>'];
    return `${openTag}${disabled ? '' : ` href="${item.linkUrl}"`}
             title="${item.description || ''}"${targetAttr}
             class="${className}${stateClasses}"
             ${disabledAttrs}
             ${additionalAttributes}>
             ${iconHtml}
             ${content}${badgeHtml}
           ${closeTag}`.replace(/\s+/g, ' ').trim();
  }

  /**
   * Set the menu data
   * @param data - Array of menu items or JSON string
   */
  setData(data: HierarchicalMenuItem[] | string): void {
    if (typeof data === 'string') {
      try {
        this.menuData = JSON.parse(data);
      } catch (e) {
        console.error("Invalid JSON data:", e);
        return;
      }
    } else if (Array.isArray(data)) {
      this.menuData = data;
    } else {
      console.error("Invalid data format. Expected array or JSON string.");
      return;
    }
  }

  /**
   * Set the icon root URL
   * @param iconRoot - Base URL for relative icon paths
   */
  setIconRoot(iconRoot: string): void {
    this.iconRoot = iconRoot || DEFAULT_ICON_ROOT;
  }

  /**
   * Get recent menu items based on lastAccessDate
   * @param limit - Maximum number of items to return (default: 4)
   * @returns Array of recent menu items
   */
  getRecentItems(limit: number = 4): HierarchicalMenuItem[] {
    // Get all items flattened
    const allItems = this.getAllItems();

    // Sort items by lastAccessDate in descending order (most recent first)
    const sortedItems = allItems.sort((a, b) => {
      const dateA = a.lastAccessDate ? new Date(a.lastAccessDate).getTime() : 0;
      const dateB = b.lastAccessDate ? new Date(b.lastAccessDate).getTime() : 0;
      return dateB - dateA;
    });

    // Return the first 'limit' items (most recent)
    return sortedItems.slice(0, limit);
  }

  /**
   * Get menu items marked as favorites
   * @returns Array of favorite menu items
   */
  getFavoriteItems(): HierarchicalMenuItem[] {
    // Get all items flattened
    const allItems = this.getAllItems();
    return allItems.filter(item => item.favorite === true);
  }

  /**
   * Check if an item has children
   * @param item - Menu item to check
   * @returns True if the item has children, false otherwise
   */
  hasChildren(item: HierarchicalMenuItem): boolean {
    return Boolean(item.items && item.items.length > 0);
  }

  /**
   * Check if an item is a parent/category item
   * @param item - Menu item to check
   * @returns True if the item is a parent/category, false otherwise
   */
  isParentItem(item: HierarchicalMenuItem): boolean {
    return this.hasChildren(item);
  }
}
